import HeaderMain from "../components/header/HeaderMain";
import NavMain from "../components/nav/NavMain";
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Col, Row, Form, FloatingLabel, Button, Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { format } from 'date-fns';
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useLocation } from 'react-router-dom';
import Select from 'react-select'; // Import the Select component
import GLOBALVAR from "../constants"; // Import GLOBALVAR constant
import LoadingSpinner from "../components/misc/LoadingSpinner";
import "../css/loan-processing.scss";

function LoanProcessing() {
    const [value, setValue] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');
    const [dateGranted, setDateGranted] = useState(null); // State to manage birthday
    const [showModal, setShowModal] = useState(false);
    const [modalHeader, setModalHeader] = useState(null);
    const [modalBody, setModalBody] = useState(null);
    const [showSpinner, setShowSpinner] = useState(false);
    const [errMessages, setErrMessages] = useState(false);
    const handleClose = () => setShowModal(false);
    const handleShow = () => setShowModal(true);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    const [formData, setFormData] = useState({
        loanProcessingType: '',
        amountGranted: '',
        dateGranted: '',
        interestRate: '',
        loanTerm: '',
        paymentType: '',
        loanInterval: '',
        serviceFee: '',
        notarialFee: '',
        docStamp: '',
        insurance: '',
        advanceInterest: true, // Add this new field with default true
        accountOfficer: '',
        loanProduct: '',
        loanLedger: '',
        loanSecurity: '',
        reference: '',
        loanType: '',
        addtl_beneficiary: '',
        addtl_contact: '',
        addtl_relationship: ''
    });
    

    const handleDateChange = (date) => {
        if(!date) return;

        setDateGranted(date);
        const formattedDate = format(date, 'MM/dd/yyyy'); 
        handleChangeInput({ target: { name: "dateGranted", value: formattedDate } });
    };

    useEffect(() => {
        let source = axios.CancelToken.source();

        const fetchOptions = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/applicationsearch?query=${value}`, {
                    cancelToken: source.token
                });
                const data = response.data;
                if (data && data.rows) {
                    const suggestions = data.rows.map(application => ({
                        label: `${application.lastName}, ${application.firstName} ${application.middleName}`,
                        value: application.id
                    }));
                    setOptions(suggestions);
                } else {
                    console.error('Invalid response structure:', data);
                    setOptions([]);
                }
            } catch (error) {
                if (!axios.isCancel(error)) {
                    console.error('Error fetching suggestions:', error);
                }
            } finally {
                setLoading(false);
            }
        };
        
        if (id) {
            const fetchApplication = async () => {
                try {
                    setLoading(true);
                    const response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/applications/${id}`);
                    const application = response.data;
                    setValue(`${application.lastName}, ${application.firstName} ${application.middleName}`);
                    setOptions([{ label: `${application.lastName}, ${application.firstName} ${application.middleName}`, value: application.id }]);
                } catch (error) {
                    if (error.response && error.response.status === 404) {
                        navigate('/dashboard');
                    } else {
                        console.error('Error retrieving application:', error);
                    }
                } finally {
                    setLoading(false);
                }
            };
            fetchApplication();
        } else {
            fetchOptions();
        }

        return () => {
            source.cancel();
        };
    }, [id, value, navigate]);

    useEffect(() => {
        if (id && !hasLoaded) { 
            setFormData(prevFormData => ({ ...prevFormData, applicantID: id }));
            setHasLoaded(true);
        }
    }, [id, hasLoaded]);


    const handleChange = (selectedOption) => {
        setValue(selectedOption ? selectedOption.label : '');
        setFormData({ ...formData, applicantID: selectedOption.value });
    };

// Modify handleChangeInput to handle select inputs differently
const handleChangeInput = (e) => {
    const { name, value } = e.target;
    
    // Handle select inputs
    const isSelectInput = e.target.tagName === 'SELECT';
    const isDefaultOption = isSelectInput && e.target.options[e.target.selectedIndex].text.startsWith('Select');
    const effectiveValue = isDefaultOption ? '' : value;

    setFormData(prevState => {
        const newState = { ...prevState };
        
        if (name === 'amountGranted') {
            const amountGranted = parseFloat(value) || 0;
            newState[name] = amountGranted;
            newState.serviceFee = 125;
            newState.notarialFee = 300;
            newState.docStamp = parseFloat((amountGranted / 200 * 1.5).toFixed(2));
            newState.insurance = parseFloat((amountGranted / 1000 * 10).toFixed(2));

            // Validate amount
            if (amountGranted <= 0) {
                setValidationErrors(prev => ({
                    ...prev,
                    [name]: 'Amount must be greater than 0'
                }));
            } else {
                setValidationErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[name];
                    return newErrors;
                });
            }
        } else {
            newState[name] = effectiveValue;
            
            // Validate required fields
            if (!effectiveValue && !['reference', 'addtl_beneficiary', 'addtl_contact', 'addtl_relationship'].includes(name)) {
                setValidationErrors(prev => ({
                    ...prev,
                    [name]: `${name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, ' $1')} is required`
                }));
            } else {
                setValidationErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[name];
                    return newErrors;
                });
            }
        }
        
        return newState;
    });

    // Add visual feedback
    const input = e.target;
    input.classList.add('was-validated');
};

// Modify the validateForm function to return both validation status and errors
const validateForm = () => {
    const errors = {};
    
    // Required field validations
    const requiredFields = [
      'loanProcessingType',
      'amountGranted',
      'dateGranted',
      'interestRate',
      'loanTerm',
      'paymentType',
      'loanInterval',
      'serviceFee',
      'notarialFee',
      'docStamp',
      'insurance',
      'accountOfficer',
      'loanProduct',
      'loanLedger',
      'loanSecurity',
      'loanType',
      'applicantID'
    ];
  
    requiredFields.forEach(field => {
      if (!formData[field]) {
        errors[field] = `${field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')} is required`;
      }
    });
  
    // Numeric validations
    if (formData.amountGranted) {
      const amount = parseFloat(formData.amountGranted);
      if (isNaN(amount)) {
        errors.amountGranted = 'Amount granted must be a number';
      } else if (amount <= 0) {
        errors.amountGranted = 'Amount granted must be greater than 0';
      }
    }
  
    if (formData.interestRate) {
      const rate = parseFloat(formData.interestRate);
      if (isNaN(rate)) {
        errors.interestRate = 'Interest rate must be a number';
      } else if (rate < 0) {
        errors.interestRate = 'Interest rate cannot be negative';
      } else if (rate > 100) {
        errors.interestRate = 'Interest rate cannot be greater than 100%';
      }
    }
  
    if (formData.loanTerm) {
      const term = Number(formData.loanTerm);
      if (!Number.isInteger(term)) {
        errors.loanTerm = 'Loan term must be a whole number';
      } else if (term <= 0) {
        errors.loanTerm = 'Loan term must be greater than 0';
      }
    }
  
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  };

// Modify the handleSubmit function to use the new validation result
const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateForm();
    setValidationErrors(validation.errors);
  
    if (!validation.isValid) {
      setModalHeader('Validation Error');
      setModalBody('Please correct the following errors:');
      setErrMessages(Object.values(validation.errors));
      handleShow();
      return;
    }
  
    setShowSpinner(true);
  
    try {
      const response = await axios.post(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/add`, formData);
      console.log(response.data);
      navigate(`/loans/${response.data.loanProcessing.id}`);
    } catch (error) {
      console.error('Error submitting form:', error);
      setModalHeader('Validation Error');
      setModalBody('');
  
      const errorsArray = error.response?.data?.error?.split(',\n') || ['An error occurred'];
      const filteredErrors = errorsArray
        .map(error => error.replace('notNull Violation: ', ''))
        .filter(error => error.trim() !== '');
      setErrMessages(filteredErrors);
      handleShow();
    } finally {
      setShowSpinner(false);
    }
  };

    return (
        <>
            {showSpinner && (<LoadingSpinner />)}
            <NavMain slug="loan-processing" parent="1" />
            <HeaderMain slug="loan-processing" />
            <div className="main-content loan-processing">
                <Modal show={showModal} onHide={handleClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>{modalHeader}</Modal.Title>
                    </Modal.Header>
                    {errMessages && (
                        <Modal.Body>
                        <ul>
                        {errMessages.map((error, index) => (
                            <li key={index}>{error.trim().replace('Validation error: ', '')}</li>
                        ))}
                        </ul>
                        </Modal.Body>
                    )}
                    {!errMessages && (
                        <Modal.Body>
                        {modalBody}
                        </Modal.Body>
                    )}
                </Modal>
                <Form onSubmit={handleSubmit}>
                    <div className="form-section">
                        <h2 className="section-title">
                            <i className="fa-solid fa-user"></i>
                            Applicant Selection
                        </h2>
                        <Row>
                            <Col>
                                <Select
                                    options={options}
                                    isLoading={loading}
                                    onInputChange={setValue}
                                    onChange={handleChange}
                                    value={options.find(option => option.label === value)}
                                    placeholder="Type a name..."
                                    isDisabled={id ? true : false}
                                    name="applicantID"
                                />
                            </Col>
                        </Row>
                    </div>
                    <div className="form-section">
                        <h2 className="section-title">
                            <i className="fa-solid fa-file-invoice-dollar"></i>
                            Loan Details
                        </h2>
                        <Row>
                            <Col>
                                <FloatingLabel controlId="floatingLoanProcessingType" label="Type of Loan">
                                    <Form.Select 
                                        name="loanProcessingType" 
                                        onChange={handleChangeInput} 
                                        onBlur={handleChangeInput}
                                        isInvalid={!!validationErrors.loanProcessingType}
                                    >
                                        <option>Select Type of Loan</option>
                                        <option value="new">New Loan</option>
                                        <option value="reloan">Reloan</option>
                                        <option value="restructure">Restructure</option>
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {validationErrors.loanProcessingType}
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Col>
                        </Row>
                        <hr className="my-4"></hr>
                        <Row className="mb-3">
                            <Col>
                                <FloatingLabel controlId="floatingAmountGranted" label="Amount Granted">
                                    <Form.Control 
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="Amount Granted" 
                                        name="amountGranted" 
                                        onChange={handleChangeInput}
                                        onKeyDown={(e) => {
                                            // Allow: backspace, delete, tab, escape, enter, decimal point
                                            if ([
                                                'Backspace',
                                                'Delete',
                                                'Tab',
                                                'Escape',
                                                'Enter',
                                                '.',
                                                ','
                                            ].includes(e.key)) {
                                                // Allow decimal point only if it doesn't exist yet
                                                if ((e.key === '.' || e.key === ',') && e.target.value.includes('.')) {
                                                    e.preventDefault();
                                                }
                                                return;
                                            }
                                            // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                                            if (
                                                ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase()) && 
                                                (e.ctrlKey || e.metaKey)
                                            ) {
                                                return;
                                            }
                                            // Block non-number inputs
                                            if (!/\d/.test(e.key)) {
                                                e.preventDefault();
                                            }
                                        }}
                                        isInvalid={!!validationErrors.amountGranted}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {validationErrors.amountGranted}
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Col>
                            <Col>
                            {dateGranted ? (
                                <DatePicker
                                className="form-control"
                                selected={dateGranted} // Set the selected date
                                onChange={(e) => {
                                    handleDateChange(e);
                                }} // Handle date change
                                dateFormat="MM/dd/yyyy" // Date format
                                peekNextMonth
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                maxDate={new Date()} // Max date is today
                                name="dateGranted"
                                />
                            ) : (
                                <FloatingLabel controlId="floatingBirthday" label="Date Granted">
                                <DatePicker
                                    className="form-control"
                                    selected={dateGranted} // Set the selected date
                                    onChange={(e) => {
                                        handleDateChange(e);
                                    }} // Handle date change
                                    dateFormat="MM/dd/yyyy" // Date format
                                    peekNextMonth
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    name="dateGranted"
                                    />
                                </FloatingLabel>
                            )}
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col>
                                <FloatingLabel controlId="floatingInterestRate" label="Interest Rate (%)">
                                    <Form.Control 
                                    type="number" 
                                    placeholder="Interest Rate" 
                                    name="interestRate" 
                                    onChange={handleChangeInput}
                                    isInvalid={!!validationErrors.interestRate}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {validationErrors.interestRate}
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Col>
                            <Col>
                                <FloatingLabel controlId="floatingLoanTerm" label="Loan Term">
                                    <Form.Control 
                                    type="number" 
                                    placeholder="Loan Term" 
                                    name="loanTerm" 
                                    onChange={handleChangeInput}
                                    isInvalid={!!validationErrors.loanTerm}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {validationErrors.loanTerm}
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Col>
                            <Col>
                                <FloatingLabel controlId="floatingPaymentType" label="Payment Type">
                                    <Form.Select name="paymentType" onChange={handleChangeInput} onBlur={handleChangeInput} isInvalid={!!validationErrors.paymentType}>
                                        <option>Select Payment Type</option>
                                        <option value="add">Add On</option>
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {validationErrors.paymentType}
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col>
                                <FloatingLabel controlId="floatingLoanInterval" label="Loan Interval">
                                    <Form.Select name="loanInterval" onChange={handleChangeInput} onBlur={handleChangeInput} isInvalid={!!validationErrors.loanInterval}>
                                        <option>Select Loan Interval</option>
                                        <option value="monthly">Monthly</option>
                                        <option value="bimonthly">Bi-Monthly</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="quarterly">Quarterly</option>
                                        <option value="semiannual">Semi-Annual</option>
                                        <option value="yearly">Yearly</option>
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {validationErrors.loanInterval}
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Col>
                        </Row>
                    </div>
                    <div className="form-section">
                        <h2 className="section-title">
                            <i className="fa-solid fa-calculator"></i>
                            Fees & Charges
                        </h2>
                        <Row className="mb-3">
                            <Col>
                                <FloatingLabel controlId="floatingServiceFee" label="Service Fee">
                                    <Form.Control 
                                    type="number" 
                                    placeholder="Service Fee" 
                                    name="serviceFee" 
                                    onChange={handleChangeInput}
                                    value={formData.serviceFee}
                                    readOnly
                                    />
                                </FloatingLabel>
                            </Col>
                            <Col>
                                <FloatingLabel controlId="floatingNotarialFee" label="Notarial Fee">
                                    <Form.Control 
                                    type="number" 
                                    placeholder="Notarial Fee" 
                                    name="notarialFee" 
                                    onChange={handleChangeInput}
                                    value={formData.notarialFee}
                                    readOnly
                                    />
                                </FloatingLabel>
                            </Col>
                            <Col>
                                <FloatingLabel controlId="floatingDocStamp" label="Doc Stamp">
                                    <Form.Control 
                                    type="number" 
                                    placeholder="Doc Stamp" 
                                    name="docStamp" 
                                    onChange={handleChangeInput}
                                    value={formData.docStamp}
                                    readOnly
                                    />
                                </FloatingLabel>
                            </Col>
                            <Col>
                                <FloatingLabel controlId="floatingInsurance" label="Insurance">
                                    <Form.Control 
                                    type="number" 
                                    placeholder="Insurance" 
                                    name="insurance" 
                                    onChange={handleChangeInput}
                                    value={formData.insurance}
                                    readOnly
                                    />
                                </FloatingLabel>
                            </Col>
                            <Col>
                                <FloatingLabel controlId="floatingAdvanceInterest" label="Advance Interest">
                                    <Form.Check
                                        type="switch"
                                        id="advanceInterestSwitch"
                                        name="advanceInterest"
                                        checked={formData.advanceInterest}
                                        onChange={(e) => {
                                            handleChangeInput({
                                                target: {
                                                    name: 'advanceInterest',
                                                    value: e.target.checked
                                                }
                                            });
                                        }}
                                        className="mt-2"
                                        label={formData.advanceInterest ? "Yes" : "No"}
                                    />
                                </FloatingLabel>
                            </Col>
                        </Row>
                    </div>
                    <div className="form-section">
                        <h2 className="section-title">
                            <i className="fa-solid fa-briefcase"></i>
                            Processing Details
                        </h2>
                        <Row className="mb-3">
                            <Col>
                                <FloatingLabel controlId="floatingAccountOfficer" label="Account Officer">
                                    <Form.Control 
                                    type="text" 
                                    placeholder="Account Officer" 
                                    name="accountOfficer" 
                                    onKeyUp={handleChangeInput}
                                    onChange={handleChangeInput}
                                    isInvalid={!!validationErrors.accountOfficer}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {validationErrors.accountOfficer}
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Col>
                            <Col>
                                <FloatingLabel controlId="floatingLoanProduct" label="Loan Product">
                                    <Form.Select name="loanProduct" onChange={handleChangeInput} onBlur={handleChangeInput} isInvalid={!!validationErrors.loanProduct}>
                                        <option>Select Loan Product</option>
                                        <option value="regular">Regular Loan</option>
                                        <option value="salary">Salary Loan</option>
                                        <option value="pension">Pension Loan</option>
                                        <option value="honorarium">Honorarium Loan</option>
                                        <option value="market">Market Vendors</option>
                                        <option value="clean">Clean Loan</option>
                                        <option value="chattel">Chattel</option>
                                        <option value="rem">REM</option>
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {validationErrors.loanProduct}
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col>
                                <FloatingLabel controlId="floatingLoanLedger" label="Loan Ledger">
                                    <Form.Control 
                                    type="text" 
                                    placeholder="Loan Ledger" 
                                    name="loanLedger" 
                                    onKeyUp={handleChangeInput}
                                    onChange={handleChangeInput}
                                    isInvalid={!!validationErrors.loanLedger}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {validationErrors.loanLedger}
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Col>
                            <Col>
                                <FloatingLabel controlId="floatingLoanSecurity" label="Loan Security">
                                    <Form.Select name="loanSecurity" onChange={handleChangeInput} onBlur={handleChangeInput} isInvalid={!!validationErrors.loanSecurity}>
                                        <option>Select Loan Security</option>
                                        <option value="unsecured">Unsecured</option>
                                        <option value="atm">Secured with ATM</option>
                                        <option value="pdc">Secured with PDC</option>
                                        <option value="clean">Clean Loan</option>
                                        <option value="chattel">Chattel Mortgage</option>
                                        <option value="residential">Real Estate Mortgage - Residential</option>
                                        <option value="commercial">Real Estate Mortgage - Commercial</option>
                                        <option value="others">Others</option>
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {validationErrors.loanSecurity}
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col>
                                <FloatingLabel controlId="floatingLoanType" label="Loan Type">
                                    <Form.Select name="loanType" onChange={handleChangeInput} onBlur={handleChangeInput} isInvalid={!!validationErrors.loanType}>
                                        <option>Select Loan Type</option>
                                        <option value="personal">Personal Loan</option>
                                        <option value="business">Business Loan</option>
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {validationErrors.loanType}
                                    </Form.Control.Feedback>
                                </FloatingLabel>
                            </Col>
                        </Row>
                    </div>
                    <div className="form-section">
                        <h2 className="section-title">
                            <i className="fa-solid fa-users"></i>
                            Additional Information
                        </h2>
                        <Row className="mb-3">
                            <Col>
                                <FloatingLabel controlId="floatingBeneficiaryName" label="Beneficiary Name">
                                    <Form.Control 
                                    type="text" 
                                    placeholder="Beneficiary Name" 
                                    name="addtl_beneficiary" 
                                    onKeyUp={handleChangeInput}
                                    onChange={handleChangeInput}
                                    />
                                </FloatingLabel>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col>
                                <FloatingLabel controlId="floatingContactNumber" label="Contact Number">
                                    <Form.Control 
                                    type="text" 
                                    placeholder="Contact Number" 
                                    name="addtl_contact" 
                                    onKeyUp={handleChangeInput}
                                    onChange={handleChangeInput}
                                    />
                                </FloatingLabel>
                            </Col>
                            <Col>
                                <FloatingLabel controlId="floatingRelationship" label="Relationship">
                                    <Form.Control 
                                    type="text" 
                                    placeholder="Relationship" 
                                    name="addtl_relationship" 
                                    onKeyUp={handleChangeInput}
                                    onChange={handleChangeInput}
                                    />
                                </FloatingLabel>
                            </Col>
                            <Col>
                                <FloatingLabel controlId="floatingReference" label="Reference">
                                    <Form.Control 
                                    type="text" 
                                    placeholder="Reference" 
                                    name="reference" 
                                    onKeyUp={handleChangeInput}
                                    onChange={handleChangeInput}
                                    />
                                </FloatingLabel>
                            </Col>
                        </Row>
                    </div>
                    <div className="form-actions">
                        <Button variant="primary" type="submit" className="submit-btn" id="submit-processing">
                            <i className="fa-solid fa-paper-plane"></i>
                            Process Loan
                        </Button>
                    </div>
                </Form>
            </div>
        </>
    );
}

export default LoanProcessing;
